import React, { useState } from 'react'
import PropTypes from 'prop-types'

import { isEmpty } from 'lodash'

import {
  Box,
  FormControlLabel,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { ExpandMore } from '@material-ui/icons'

import { LoadingFeedback } from 'components'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import helpers from 'helpers'
import useStyles from './styles'

const FILTER_ALL = 'all'
const FILTER_DEPARTMENT = 'department'
const FILTER_PROJECT = 'project'

const PrivacyByDesignReportForm = ({ refresh }) => {
  const [filterType, setFilterType] = useState(FILTER_ALL)
  const [department, setDepartment] = useState(null)
  const [project, setProject] = useState(null)
  const [loading, setLoading] = useState(false)

  const classes = useStyles()
  const snackbar = useSnackbar()

  const { response: deptResponse } = useFetch(
    service.dponet.departments.get,
    {},
    [],
  )
  const { response: projectResponse } = useFetch(
    service.dponet.privacyByDesign.get,
    { perPage: 9999 },
    [],
  )

  const departments = deptResponse?.data?.departments || []
  const projects = projectResponse?.data?.privacyByDesigns || []

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (isEmpty(projects)) {
        snackbar.open({
          message: 'Não existem projetos de Privacy by Design cadastrados.',
          variant: 'error',
        })
        return
      }

      let params = {}
      if (filterType === FILTER_DEPARTMENT && department) {
        const hasPbdsForDepartment = projects.some(
          (p) => p.department?.id === department.id,
        )
        if (!hasPbdsForDepartment) {
          snackbar.open({
            message:
              'Não existem projetos de Privacy by Design para o departamento selecionado.',
            variant: 'error',
          })
          return
        }
        params = { departmentId: department.id }
      } else if (filterType === FILTER_PROJECT && project) {
        params = { privacyByDesignId: project.id }
      }

      const response = await service.dponet.privacyByDesign.report(params)

      snackbar.open({
        message:
          response?.data?.message ||
          'Relatório sendo gerado. Você receberá o PDF por e-mail em breve.',
        variant: response?.status === 200 ? 'warning' : 'success',
      })
      refresh()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(
            error?.response?.data?.error,
            false,
          ) || 'Não foi possível gerar o relatório de Privacy By Design.',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <LoadingFeedback open={loading} />
      <form id="report-form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <InputLabel className={classes.label}>
              Escolha o escopo do relatório:
            </InputLabel>
            <RadioGroup
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value)
                setDepartment(null)
                setProject(null)
              }}
            >
              <FormControlLabel
                value={FILTER_ALL}
                control={<Radio classes={{ checked: classes.checked }} />}
                className={classes.radioLabel}
                label="Todos os projetos"
              />
              <FormControlLabel
                value={FILTER_DEPARTMENT}
                control={<Radio classes={{ checked: classes.checked }} />}
                className={classes.radioLabel}
                label="Por departamento"
              />
              <FormControlLabel
                value={FILTER_PROJECT}
                control={<Radio classes={{ checked: classes.checked }} />}
                className={classes.radioLabel}
                label="Por projeto específico"
              />
            </RadioGroup>
          </Grid>

          {filterType === FILTER_DEPARTMENT && (
            <Grid item xs={12} sm={6}>
              <Autocomplete
                popupIcon={<ExpandMore />}
                options={departments}
                getOptionLabel={(option) => option.name}
                getOptionSelected={(option, value) => option.id === value.id}
                value={department}
                onChange={(_, value) => setDepartment(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Departamento"
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </Grid>
          )}

          {filterType === FILTER_PROJECT && (
            <Grid item xs={12} sm={6}>
              <Autocomplete
                popupIcon={<ExpandMore />}
                options={projects}
                getOptionLabel={(option) => `#${option.id} - ${option.name}`}
                getOptionSelected={(option, value) => option.id === value.id}
                value={project}
                onChange={(_, value) => setProject(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Projeto"
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
      </form>
    </>
  )
}

PrivacyByDesignReportForm.propTypes = {
  refresh: PropTypes.func,
}

PrivacyByDesignReportForm.defaultProps = {
  refresh: () => {},
}

export default PrivacyByDesignReportForm
